/**
 * src/App.js
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sentry from './lib/sentry';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PlanProvider } from './context/PlanContext';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Welcome from './pages/Welcome';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailPending from './pages/VerifyEmailPending';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ConfigEditor from './pages/ConfigEditor';
import VersionHistory from './pages/VersionHistory';
import Users from './pages/Users';
import UserDetail from './pages/UserDetail';
import ApiKeys from './pages/ApiKeys';
import Events from './pages/Events';
import Analytics from './pages/Analytics';
import AnalyticsReport from './pages/AnalyticsReport';
import AnalyticsReportCreate from './pages/AnalyticsReportCreate';
import './styles/globals.css';

function ContentFallback() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh', textAlign: 'center', padding: '2rem'
    }}>
      <div>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: 'var(--text-primary, #333)' }}>
          This page encountered an error
        </h2>
        <p style={{ color: 'var(--text-secondary, #666)', marginBottom: '1.5rem' }}>
          Try reloading the page, or navigate to another page using the sidebar.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.5rem 1.5rem', fontSize: '0.9rem', cursor: 'pointer',
            background: 'var(--accent, #4f46e5)', color: '#fff',
            border: 'none', borderRadius: '6px'
          }}
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

function ProtectedLayout() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace />;

  // Allow Welcome page even if unverified (user needs to see API keys)
  if (user.emailVerified === false && location.pathname !== '/welcome') {
    return <Navigate to="/verify-email-pending" replace />;
  }

  return (
    <PlanProvider>
      <div className="app-layout">
        <Sidebar />
        <main className="main-content">
          <Sentry.ErrorBoundary fallback={<ContentFallback />} key={location.pathname}>
            <Routes>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/config" element={<ConfigEditor />} />
              <Route path="/config/history" element={<VersionHistory />} />
              <Route path="/config/history/:id" element={<ConfigEditor />} />
              <Route path="/events" element={<Events />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/analytics/new" element={<AnalyticsReportCreate />} />
              <Route path="/analytics/edit/:id" element={<AnalyticsReportCreate />} />
              <Route path="/analytics/:id" element={<AnalyticsReport />} />
              {user.role === 'admin' && (
                <>
                  <Route path="/users" element={<Users />} />
                  <Route path="/users/:id" element={<UserDetail />} />
                  <Route path="/api-keys" element={<ApiKeys />} />
                </>
              )}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Sentry.ErrorBoundary>
        </main>
      </div>
    </PlanProvider>
  );
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" replace /> : <Login />}
      />
      <Route
        path="/signup"
        element={user ? <Navigate to="/dashboard" replace /> : <Signup />}
      />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route
        path="/forgot-password"
        element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
      />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Logged-in but possibly unverified */}
      <Route
        path="/verify-email-pending"
        element={
          !user ? <Navigate to="/login" replace /> :
          user.emailVerified ? <Navigate to="/dashboard" replace /> :
          <VerifyEmailPending />
        }
      />

      {/* Protected routes (require login + verified email) */}
      <Route path="/*" element={<ProtectedLayout />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
